import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import {
  Box,
  Toolbar,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid
} from "@mui/material";
import { Add, Refresh, Edit } from "@mui/icons-material";
import {
  getAccounts,
  saveAccount,
  resetSaveStatus,
  getAccountById,
} from "../../redux/masters/accountSlice";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";
import { getOptions } from "../../redux/masters/optionSlice";
import { OptionsStatus } from "../../constants/enum";
import { getOptionValueByText } from "../../utils/optionUtils";

const AccountMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showSuccess, showError } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const { data, loading, error, saveStatus } = useSelector(
    (state) => state.account || {}
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    accountGroupId: "",
    accountName: "",
    accountCode: "",
    accountType: "",
    active: true,
  });

  const optionKey = OptionsStatus.AccountMaster;
  const {
    data: loanOptions = {},
    loading: optionLoading,
    error: optionError,
  } = useSelector((state) => state.option || {});
  const optionData = loanOptions[optionKey] || [];

  useEffect(() => {
    dispatch(getAccounts());
  }, [dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      await ensureAccountOptionsLoaded();
      setOpenDialog(true);
    } catch (err) {
      showError("Failed to load account options");
    } finally {
      setAddLoading(false);
    }
  }

  const ensureAccountOptionsLoaded = async () => {
      if (!optionData || optionData.length === 0) {
        try {
          await dispatch(getOptions([optionKey])).unwrap();
        } catch (err) {
          showError("Failed to load options");
          throw err;
        }
      }
    };

  const handleRefresh = () => {
    dispatch(getAccounts());
    showSuccess("Data refreshed successfully");
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAccountSave = (accountData) => {
    dispatch(saveAccount(accountData));
  };

  useEffect(() => {
    if (saveStatus === "success") {
      setFormData({
        id: null,
        accountGroupId: "",
        accountName: "",
        accountCode: "",
        accountType: "",
        active: true,
      });
      dispatch(resetSaveStatus());
    }
  }, [saveStatus, dispatch]);

  const handleEditClick = (account) => {
    if (account.editable) {
      if (!optionData || optionData.length === 0) {
        dispatch(getOptions([OptionsStatus.AccountMaster])).then(() => {
          fetchAndSetAccount(account.id);
        });
      } else {
        fetchAndSetAccount(account.id);
      }
    }
  };

  const fetchAndSetAccount = (encodedId) => {
    dispatch(getAccountById(btoa(encodedId.toString()))).then((result) => {
      if (result.payload && result.payload.data) {
        const accountData = result.payload.data;

        const matchedType = getOptionValueByText(
          optionData,
          "Account Type",
          accountData.accountType
        );
        const matchedGroup = getOptionValueByText(
          optionData,
          "AccountGroup",
          accountData.accountGroupName
        );

        setFormData({
          id: accountData.id,
          accountGroupId: matchedGroup || "",
          accountName: accountData.accountName || "",
          accountCode: accountData.accountCode || "",
          accountType: matchedType || "",
          active: accountData.active !== undefined ? accountData.active : true,
        });

        setOpenDialog(true);
      }
    });
  };

  useEffect(() => {
    console.log("Updated formData:", formData);
  }, [formData]);

  return (
    <Box sx={{ display: "flex" }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/masters/account"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: 64 }} />

        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Account Master
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <TableSkeleton rows={5} columns={5} />
            ) : error ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  "& .MuiAlert-message": {
                    fontSize: "0.875rem",
                  },
                }}
              >
                {error}
              </Alert>
            ) : (
              <EnhancedTable
                data={data.accounts || []}
                columns={[
                  {
                    id: "accountCode",
                    label: "Code",
                    sortable: true,
                    render: (value) => (
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        {value}
                      </Typography>
                    ),
                  },
                  {
                    id: "accountName",
                    label: "Name",
                    sortable: true,
                    render: (value) => (
                      <Typography
                        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        {value}
                      </Typography>
                    ),
                  },
                  {
                    id: "accountType",
                    label: "Type",
                    sortable: true,
                    render: (value) => (
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        {value}
                      </Typography>
                    ),
                  },
                  {
                    id: "updatedAt",
                    label: "Updated At",
                    sortable: true,
                    render: (value) => (
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        {value}
                      </Typography>
                    ),
                  },
                  {
                    id: "active",
                    label: "Status",
                    sortable: true,
                    render: (value) =>
                      value ? (
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      ) : (
                        <Chip
                          label="Inactive"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      ),
                  },
                  {
                    id: "editable",
                    label: "Edit",
                    sortable: false,
                    render: (_, row) => (
                      <Tooltip
                        title={
                          row.editable
                            ? "Edit Account"
                            : "Account is not editable"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={
                              row.editable
                                ? () => handleEditClick(row)
                                : undefined
                            }
                            disabled={!row.editable}
                          >
                            <Edit />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                ]}
                title="Accounts"
                loading={loading}
                emptyMessage="No accounts found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Account</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
          <TextField
            margin="dense"
            name="accountName"
            label="Account Name"
            fullWidth
            size="small"
            value={formData.accountName}
            onChange={handleChange}
          />
          </Grid>

          <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
          <TextField
            margin="dense"
            name="accountCode"
            label="Account Code"
            fullWidth
            size="small"
            value={formData.accountCode}
            onChange={handleChange}
          />
           </Grid>

           <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
          <TextField
            select
            margin="dense"
            name="accountGroupId"
            label="Account Group"
            fullWidth
            size="small"
            value={formData.accountGroupId}
            onChange={handleChange}
          >
            {(
              optionData.find((opt) => opt.optionName === "AccountGroup")
                ?.values || []
            ).map((group) => (
              <MenuItem key={group.id} value={group.optionValue}>
                {group.optionText}
              </MenuItem>
            ))}
          </TextField>

          </Grid>

           <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
          <TextField
            select
            margin="dense"
            name="accountType"
            label="Account Type"
            fullWidth
            size="small"
            value={formData.accountType || ""}
            onChange={handleChange}
          >
            {(
              optionData.find((opt) => opt.optionName === "Account Type")
                ?.values || []
            ).map((type) => (
              <MenuItem key={type.id} value={type.optionValue}>
                {type.optionText}
              </MenuItem>
            ))}
          </TextField>
          </Grid>
        </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleAccountSave(formData)}
            variant="contained"
            sx={{
              backgroundColor: "#f74d5e",
              "&:hover": {
                backgroundColor: "#d43c4b",
              },
              color: "#fff",
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountMaster;
